// Explore's field: the search as chips, and a place to type. Clauses the query
// language reads apply as typed. Other words are translated into clauses
// (lib/exploreTranslate.js) and shown, with how many traces they would leave,
// before Enter applies them; the words themselves can be searched for instead.
// `/` finds from anywhere on the page.
import { useCallback, useEffect, useRef, useState } from "react";
import { clausesOf } from "../SearchPalette.jsx";
import { useQuery } from "../../lib/dataClient.js";
import { addClauses, isSearchSyntax, translateToSearch } from "../../lib/exploreTranslate.js";
import { appendClause } from "../../lib/workspaceSearch.js";

const NUM = new Intl.NumberFormat("en-US");
const CHIP = "inline-flex h-[28px] max-w-full items-center gap-[6px] rounded-[6px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] pl-[9px] pr-[5px] text-[13px] whitespace-nowrap";
const KBD = "grid h-[20px] min-w-[20px] shrink-0 place-items-center rounded-[5px] border border-[color:var(--line)] px-[5px] font-[inherit] text-[11px] text-[color:var(--muted-soft)]";

const cap = (w) => (w ? w[0].toUpperCase() + w.slice(1) : "");
// A chip's words: its field ("Person is", "Group is not"), and its value.
function chipWords(pill) {
  if (!pill.field) return { lead: pill.negated ? "Not" : "Words", value: pill.value };
  return { lead: `${cap(pill.field)} is${pill.negated ? " not" : ""}`, value: pill.value };
}
const typingIn = (el) => !!el?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"])');

export function Chip({ pill, onRemove = null }) {
  const { lead, value } = chipWords(pill);
  return (
    <span className={CHIP} data-explore-chip="">
      <span className="text-[color:var(--muted)]">{lead}</span>
      <span className="min-w-0 truncate text-[color:var(--ink)]">{value}</span>
      {onRemove ? (
        <button type="button" aria-label={`Remove ${lead} ${value}`} onClick={onRemove} className="grid h-[16px] w-[16px] place-items-center rounded-[4px] text-[color:var(--muted-soft)] hover:bg-[color-mix(in_srgb,var(--ink)_7%,transparent)] hover:text-[color:var(--ink)]">
          <svg viewBox="0 0 16 16" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" /></svg>
        </button>
      ) : null}
    </span>
  );
}

export default function ExploreQuery({ search, onSearch, range }) {
  const [text, setText] = useState("");
  const [focus, setFocus] = useState(false);
  // What the typed words became: translating, a proposal, or no proposal.
  const [stage, setStage] = useState({ kind: "idle" });
  const [pick, setPick] = useState(0);
  const input = useRef(null);
  const run = useRef(0);
  const { pills, without } = clausesOf(search);
  const proposed = stage.kind === "proposed" ? addClauses(search, stage.search) : null;
  const count = useQuery("getOverviewMetrics", { scopePath: [], range, query: proposed || "", byTrace: true }, { enabled: !!proposed });
  const options = stage.kind === "proposed" ? ["proposal", "words"] : stage.kind === "none" ? ["words"] : [];

  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== "/" || event.defaultPrevented || event.metaKey || event.ctrlKey || typingIn(event.target)) return;
      event.preventDefault();
      input.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const reset = useCallback(() => {
    run.current += 1;
    setStage({ kind: "idle" });
    setPick(0);
  }, []);
  const apply = useCallback((next) => {
    onSearch(next);
    setText("");
    reset();
  }, [onSearch, reset]);

  // The proposal, or the words as a phrase to find in the traces' prompts.
  const choose = (i) => {
    if (options[i] === "proposal") apply(proposed);
    else if (options[i] === "words") apply(appendClause(search, `"${text.trim().replace(/"/g, "")}"`));
  };
  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    if (options.length) {
      choose(pick);
      return;
    }
    if (isSearchSyntax(t)) {
      apply(addClauses(search, t));
      return;
    }
    const mine = ++run.current;
    setStage({ kind: "translating" });
    const out = await translateToSearch(t, { range });
    if (run.current !== mine) return;
    setPick(0);
    setStage(out.search ? { kind: "proposed", search: out.search, leftOver: out.leftOver } : { kind: "none" });
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submit();
    } else if (event.key === "Escape") {
      if (stage.kind !== "idle") reset();
      else input.current?.blur();
    } else if (event.key === "ArrowDown" && options.length) {
      event.preventDefault();
      setPick((p) => Math.min(options.length - 1, p + 1));
    } else if (event.key === "ArrowUp" && options.length) {
      event.preventDefault();
      setPick((p) => Math.max(0, p - 1));
    } else if (event.key === "Backspace" && !text && pills.length) {
      onSearch(without(pills.length - 1));
    }
  };

  const proposalPills = stage.kind === "proposed" ? clausesOf(stage.search).pills : [];
  const traces = count.data?.sessions;
  const open = stage.kind !== "idle" && !!text.trim();
  return (
    <div className="relative min-w-0 flex-1" data-explore-query="">
      <div
        onClick={() => input.current?.focus()}
        className={`flex min-h-[44px] cursor-text items-start gap-[8px] rounded-[7px] border bg-[color:var(--control-bg)] px-[10px] py-[7px] ${focus ? "border-[color:var(--green)]" : "border-[color:var(--line)] hover:border-[color:var(--line-strong)]"}`}
      >
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="mt-[6px] h-[16px] w-[16px] shrink-0 text-[color:var(--muted-soft)]"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-[6px]">
          {pills.map((pill, i) => <Chip key={`${pill.text}:${pill.negated}`} pill={pill} onRemove={(e) => { e.stopPropagation(); onSearch(without(i)); }} />)}
          <input
            ref={input}
            value={text}
            onChange={(e) => { setText(e.target.value); if (stage.kind !== "idle") reset(); }}
            onKeyDown={onKeyDown}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            placeholder={pills.length ? "Add a filter" : "Search traces by anything, or ask a question"}
            aria-label="Search traces"
            autoComplete="off"
            spellCheck={false}
            className="h-[28px] min-w-[160px] flex-1 border-0 bg-transparent p-0 text-[15px] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
          />
        </div>
        <kbd aria-hidden="true" className={`mt-[4px] ${KBD}`}>/</kbd>
      </div>
      {open ? (
        <div role="listbox" aria-label="Filters for what you typed" className="absolute inset-x-0 top-[calc(100%+6px)] z-[30] rounded-[10px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] p-[4px] shadow-[0_12px_32px_rgb(27_24_19_/_14%)]">
          {stage.kind === "translating" ? (
            <div className="flex min-h-[40px] items-center px-[10px] text-[13px] text-[color:var(--muted)]" aria-busy="true">Translating to filters</div>
          ) : null}
          {options.map((option, i) => (
            <div
              key={option}
              role="option"
              aria-selected={pick === i}
              onMouseEnter={() => setPick(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(i); }}
              className={`flex min-h-[44px] cursor-pointer items-center gap-[10px] rounded-[7px] px-[10px] py-[6px] text-[13px] ${pick === i ? "bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}
            >
              {option === "proposal" ? (
                <>
                  <span className="grid min-w-0 gap-[5px]">
                    <span className="flex min-w-0 flex-wrap gap-[6px]">{proposalPills.map((pill) => <Chip key={`${pill.text}:${pill.negated}`} pill={pill} />)}</span>
                    {/* What the words asked that no filter can say, so it is not dropped unseen. */}
                    {stage.leftOver ? <span className="text-[12px] text-[color:var(--muted)]" data-explore-left-over="">No filter for &quot;{stage.leftOver}&quot;</span> : null}
                  </span>
                  <span className="flex-1" />
                  {traces != null ? <span className="whitespace-nowrap tabular-nums"><span className="font-medium text-[color:var(--ink)]">{NUM.format(traces)}</span> traces</span> : null}
                  <kbd className={KBD}>Enter</kbd>
                </>
              ) : (
                <span>Search for these words in traces</span>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
